// Subscribers management functions
import { collection, getDocs, doc, deleteDoc, updateDoc, query, orderBy, where, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './config';

/**
 * Generate unsubscribe token for a subscriber
 */
const generateUnsubscribeToken = (subscriberId, email) => {
  // Simple token: base64 encoded subscriber ID + email hash
  // In production, you might want to use a more secure method
  const token = btoa(`${subscriberId}:${email}`).replace(/[+/=]/g, '');
  return token;
};

/**
 * Decode unsubscribe token
 */
const decodeUnsubscribeToken = (token) => {
  try {
    // Add padding if needed
    let paddedToken = token;
    while (paddedToken.length % 4 !== 0) {
      paddedToken += '=';
    }
    const decoded = atob(paddedToken);
    const [subscriberId, email] = decoded.split(':');
    return { subscriberId, email };
  } catch (error) {
    return null;
  }
};

/**
 * Get all subscribers
 */
export const getAllSubscribers = async (filters = {}) => {
  try {
    const subscribersRef = collection(db, 'subscribers');
    let q = query(subscribersRef, orderBy('createdAt', 'desc'));
    
    if (filters.status) {
      q = query(q, where('status', '==', filters.status));
    }
    
    if (filters.source) {
      q = query(q, where('source', '==', filters.source));
    }
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting subscribers:', error);
    return [];
  }
};

/**
 * Get subscriber by email
 */
export const getSubscriberByEmail = async (email) => {
  try {
    const subscribersRef = collection(db, 'subscribers');
    const q = query(subscribersRef, where('email', '==', email.trim().toLowerCase()));
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      return null;
    }
    
    return {
      id: snapshot.docs[0].id,
      ...snapshot.docs[0].data()
    };
  } catch (error) {
    console.error('Error getting subscriber by email:', error);
    return null;
  }
};

/**
 * Get subscriber by token (for unsubscribe)
 */
export const getSubscriberByToken = async (token) => {
  try {
    const decoded = decodeUnsubscribeToken(token);
    if (!decoded) {
      return null;
    }
    
    const subscriberRef = doc(db, 'subscribers', decoded.subscriberId);
    const subscriberSnap = await getDoc(subscriberRef);
    
    if (!subscriberSnap.exists()) {
      return null;
    }
    
    const subscriber = { id: subscriberSnap.id, ...subscriberSnap.data() };
    
    // Verify email matches
    if (subscriber.email.toLowerCase() !== decoded.email.toLowerCase()) {
      return null;
    }
    
    return subscriber;
  } catch (error) {
    console.error('Error getting subscriber by token:', error);
    return null;
  }
};

/**
 * Generate unsubscribe URL for a subscriber
 */
export const getUnsubscribeUrl = (subscriberId, email) => {
  const token = generateUnsubscribeToken(subscriberId, email);
  const baseUrl = window.location.origin || 'https://freespiritfabiha.com';
  return `${baseUrl}/unsubscribe/${token}`;
};

/**
 * Unsubscribe a subscriber
 */
export const unsubscribeSubscriber = async (token) => {
  try {
    const subscriber = await getSubscriberByToken(token);
    
    if (!subscriber) {
      return { success: false, error: 'Invalid unsubscribe link' };
    }
    
    const subscriberRef = doc(db, 'subscribers', subscriber.id);
    await updateDoc(subscriberRef, {
      status: 'unsubscribed',
      unsubscribedAt: serverTimestamp()
    });
    
    return { success: true, subscriber };
  } catch (error) {
    console.error('Error unsubscribing:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete a subscriber
 */
export const deleteSubscriber = async (subscriberId) => {
  try {
    const subscriberRef = doc(db, 'subscribers', subscriberId);
    await deleteDoc(subscriberRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting subscriber:', error);
    return { success: false, error: error.message };
  }
};

